// ProceduresSection — SID / STAR / IAP picker for PlanPanel.
//
// Auto-detects departure (first airport in route) and destination (last
// airport in route) from planStore. For each, fetches the airport's
// procedures from /api/procedures via proceduresStore. The user picks:
//   - a SID + runway transition for departure
//   - a STAR + enroute transition for destination
//   - an IAP + transition for destination
//
// Selecting / changing a procedure splices the expanded fix list into
// the route via spliceProcedureIntoRoute. Picking "(none)" removes the
// previously-inserted expansion.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFetch } from '../hooks/useFetch';
import { Loader2, AlertCircle, FileText, BookOpen, Map as MapIcon } from '../shell/Icon';
import { usePlanStore, type PlanPoint } from '../stores/planStore';
import { useUiStore } from '../stores/uiStore';
import { useProceduresStore, filterByRouteType } from '../stores/proceduresStore';
import {
  expandSid, expandStar, expandIap,
  spliceProcedureIntoRoute,
  sameAirportIcao,
  type ExtendedPlanPoint,
} from '../lib/procedureExpansion';
import type { Procedure } from '../lib/cifp/cifp';
import {
  fetchAirportPlates,
  fetchAirportGeorefStatus,
  plateGeorefQuality,
  type PlatesResponse,
  type PlateRecord,
  type PlateGeorefIndexRow,
} from '../lib/plates';
import { findPlateForProcedure, parseIapIdent } from '../lib/procedurePlateMatch';
import { GeorefBadge } from '../components/GeorefBadge';
import { PlateViewer } from '../plates/PlateViewer';
import { ProcedureBriefPanel } from '../plates/ProcedureBriefPanel';
import { generateBrief, type ProcedureBrief } from '../lib/procedureBrief';

export default function ProceduresSection() {
  const route = usePlanStore((s) => s.route);

  // Active plate-viewer state. Lives at the section level so any block
  // can open the viewer; only one plate visible at a time.
  const [activePlate, setActivePlate] = useState<{ cycle: string; pdfName: string; title: string; subtitle: string } | null>(null);
  // Active brief panel.
  const [activeBrief, setActiveBrief] = useState<ProcedureBrief | null>(null);

  // Anchor airports
  const departure = useMemo(() => route.find((p) => p.kind === 'airport') ?? null, [route]);
  const destination = useMemo(() => {
    for (let i = route.length - 1; i >= 0; i--) {
      if (route[i].kind === 'airport') return route[i];
    }
    return null;
  }, [route]);

  return (
    <div className="space-y-3">
      {/* Departure procedure (SID) */}
      <ProcedureBlock
        title="Departure (SID)"
        airport={departure}
        routeType="SID"
        onOpenPlate={setActivePlate}
        onOpenBrief={setActiveBrief}
      />

      {/* Arrival procedure (STAR) */}
      <ProcedureBlock
        title="Arrival (STAR)"
        airport={destination}
        routeType="STAR"
        onOpenPlate={setActivePlate}
        onOpenBrief={setActiveBrief}
      />

      {/* Approach (IAP) */}
      <ProcedureBlock
        title="Approach (IAP)"
        airport={destination}
        routeType="IAP"
        onOpenPlate={setActivePlate}
        onOpenBrief={setActiveBrief}
      />

      {!departure && !destination && (
        <div className="text-[11px] text-cockpit-400 italic px-1 py-1.5">
          Add a departure and destination airport above to pick procedures.
        </div>
      )}

      {activePlate && (
        <PlateViewer
          cycle={activePlate.cycle}
          pdfName={activePlate.pdfName}
          title={activePlate.title}
          subtitle={activePlate.subtitle}
          onClose={() => setActivePlate(null)}
        />
      )}
      {activeBrief && (
        <div className="absolute inset-0 z-50">
          <ProcedureBriefPanel
            brief={activeBrief}
            onClose={() => setActiveBrief(null)}
          />
        </div>
      )}
    </div>
  );
}

interface BlockProps {
  title: string;
  airport: PlanPoint | null;
  routeType: 'SID' | 'STAR' | 'IAP';
  onOpenPlate?: (p: { cycle: string; pdfName: string; title: string; subtitle: string }) => void;
  onOpenBrief?: (brief: ProcedureBrief) => void;
}

function ProcedureBlock({ title, airport, routeType, onOpenPlate, onOpenBrief }: BlockProps) {
  const loadAirport = useProceduresStore((s) => s.loadAirport);
  const loadingSet  = useProceduresStore((s) => s.loadingAirports);
  const errors      = useProceduresStore((s) => s.errors);
  const cache       = useProceduresStore((s) => s.byAirport);

  const route = usePlanStore((s) => s.route);
  const planSet = usePlanStore.setState;

  useEffect(() => {
    if (airport?.kind === 'airport') void loadAirport(airport.ident);
  }, [airport?.ident, airport?.kind, loadAirport]);

  // Plates for this airport (lazy fetch on demand for the 'Open plate' link).
  const { data: plates, loading: platesLoading } = useFetch(
    () => airport?.kind === 'airport'
      ? fetchAirportPlates(airport.ident).catch(() => null as PlatesResponse | null)
      : Promise.resolve(null as PlatesResponse | null),
    [airport?.ident, airport?.kind],
  );
  // Track 2 (2026-05-16): per-airport georef status so PlateLink can
  // render a confidence badge next to the 'Open plate' link.
  const { data: georefMap } = useFetch<Map<string, PlateGeorefIndexRow>>(
    () => {
      if (airport?.kind !== 'airport' || !plates?.cycle) {
        return Promise.resolve(new Map<string, PlateGeorefIndexRow>());
      }
      return fetchAirportGeorefStatus(airport.ident, plates.cycle)
        .catch(() => new Map<string, PlateGeorefIndexRow>());
    },
    [airport?.ident, airport?.kind, plates?.cycle],
  );

  if (!airport || airport.kind !== 'airport') {
    return (
      <div className="rounded-lg border border-cockpit-700 bg-cockpit-800/50 p-2.5">
        <div className="text-[11px] font-semibold text-cockpit-400 uppercase tracking-wider mb-1">{title}</div>
        <div className="text-[11px] text-cockpit-400 italic">No airport in route.</div>
      </div>
    );
  }

  const icao = airport.ident;
  const loading = loadingSet.has(icao);
  const err = errors.get(icao);
  const cached = cache.get(icao);
  const allProcs = cached?.procedures ?? [];
  const procs = filterByRouteType(allProcs, routeType);

  // Read currently-selected procedure for this airport+type out of the route.
  // Use lenient airport-ident comparison so 'O46' (plan row) and 'KO46'
  // (procedureTag from CIFP) read as the same airport. Bug #3 (2026-05-16).
  const existing = (route as ExtendedPlanPoint[]).find((p) =>
    p.procedureTag
    && sameAirportIcao(p.procedureTag.airportIcao, icao)
    && p.procedureTag.routeType === routeType,
  );
  const selectedProcIdent = existing?.procedureTag?.procIdent ?? '';
  const selectedTransIdent = existing?.procedureTag?.transIdent ?? '';

  const selectedProc = procs.find((p) => p.procIdent === selectedProcIdent) ?? null;

  function applySelection(proc: Procedure | null, transIdent: string) {
    const current = (usePlanStore.getState().route as ExtendedPlanPoint[]);
    if (!proc) {
      // Removing the procedure: splice with an empty expansion.
      const next = spliceProcedureIntoRoute(current, [], icao, routeType);
      planSet({ route: next });
      return;
    }
    let expanded: ExtendedPlanPoint[];
    if (routeType === 'SID') {
      // Seed the altitude-terminator cursor with the departure airport so
      // a VA-first SID (e.g. O46 FOBRO1) synthesizes the first VA leg
      // from the airport instead of dropping it as a placeholder — which
      // would leave a visible gap on the map between the airport and the
      // first published fix.
      expanded = expandSid(
        proc,
        transIdent,
        undefined,
        airport ? { lat: airport.lat, lon: airport.lon } : undefined,
      );
    } else if (routeType === 'STAR') {
      expanded = expandStar(proc, transIdent);
    } else {
      expanded = expandIap(proc, transIdent);
    }
    const next = spliceProcedureIntoRoute(current, expanded, icao, routeType);
    planSet({ route: next });
  }

  return (
    <div className="rounded-lg border border-cockpit-700 bg-cockpit-800/50 p-2.5 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-semibold text-cockpit-400 uppercase tracking-wider">{title}</span>
        <span className="text-[10px] font-mono text-cockpit-300">{icao}</span>
      </div>

      {loading && (
        <div className="flex items-center gap-1.5 text-[11px] text-cockpit-300">
          <Loader2 size={12} className="animate-spin" /> Loading procedures…
        </div>
      )}

      {err && !loading && (
        <div className="flex items-start gap-1.5 text-[11px] text-red-400">
          <AlertCircle size={12} className="mt-0.5 flex-shrink-0" /> {err}
        </div>
      )}

      {!loading && !err && procs.length === 0 && (
        <div className="text-[11px] text-cockpit-400 italic">
          No {routeType}s published for {icao}.
        </div>
      )}

      {!loading && procs.length > 0 && (
        <>
          <select
            value={selectedProcIdent}
            onChange={(e) => {
              const proc = procs.find((p) => p.procIdent === e.target.value) ?? null;
              // Pick a sensible default transition when changing procedure.
              const firstTrans = proc?.transitions[0]?.ident ?? '';
              applySelection(proc, firstTrans);
            }}
            className="w-full bg-cockpit-700 border border-cockpit-600 rounded-md text-sm px-2 py-1.5"
          >
            <option value="">— none —</option>
            {procs.map((p) => (
              <option key={p.procIdent} value={p.procIdent}>
                {procedureLabel(p)}
              </option>
            ))}
          </select>

          {selectedProc && (
            <select
              value={selectedTransIdent}
              onChange={(e) => applySelection(selectedProc, e.target.value)}
              className="w-full bg-cockpit-700 border border-cockpit-600 rounded-md text-sm px-2 py-1.5"
            >
              {selectedProc.transitions.map((t) => (
                <option key={t.ident || '(common)'} value={t.ident}>
                  {transitionLabel(t.ident, t.kind, routeType)}
                </option>
              ))}
            </select>
          )}

          {selectedProc && existing?.procedureTag && (
            <div className="flex items-center justify-between gap-2">
              <div className="text-[10px] text-cockpit-400 font-mono">
                {countExpandedFixes(route as ExtendedPlanPoint[], icao, routeType)} fixes inserted
              </div>
              <div className="flex items-center gap-2">
                {onOpenBrief && (
                  <button
                    type="button"
                    onClick={() => {
                      const brief = generateBrief(selectedProc, selectedTransIdent);
                      onOpenBrief(brief);
                    }}
                    className="flex items-center gap-1 text-[11px] text-cockpit-300 hover:text-cockpit-100"
                  >
                    <BookOpen size={11} />
                    <span>Brief</span>
                  </button>
                )}
                <PlateLink
                  proc={selectedProc}
                  plates={plates}
                  loading={platesLoading}
                  onOpen={onOpenPlate}
                  airportIcao={icao}
                  georefMap={georefMap ?? new Map()}
                />
                <SendToMapLink
                  proc={selectedProc}
                  plates={plates}
                  loading={platesLoading}
                  airportIcao={icao}
                  georefMap={georefMap ?? new Map()}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function transitionLabel(ident: string, kind: 'runway' | 'transition' | 'common', routeType: string): string {
  if (kind === 'common') return `${routeType === 'IAP' ? 'Common segment' : 'All transitions'}`;

  // Runway transitions: CIFP prefixes with a digit (e.g. '4RW10L'),
  // strip it for display.
  if (kind === 'runway') {
    const cleaned = ident.replace(/^\d/, '');
    return cleaned.startsWith('RW') ? `Runway ${cleaned.slice(2)}` : cleaned;
  }

  // Named transitions: IAP transitions in CIFP often carry an 'A'
  // (Approach) class prefix (e.g. 'AHONE' = approach via HONEZ fix).
  // Strip the leading 'A' for IAP transitions only — SID/STAR named
  // transitions are typically already the bare fix ident.
  if (routeType === 'IAP' && /^A[A-Z]/.test(ident)) {
    return `via ${ident.substring(1)}`;
  }
  return `via ${ident}`;
}

function countExpandedFixes(
  route: ExtendedPlanPoint[],
  airportIcao: string,
  routeType: string,
): number {
  return route.filter((p) => p.procedureTag
    && p.procedureTag.airportIcao === airportIcao
    && p.procedureTag.routeType === routeType).length;
}

interface PlateLinkProps {
  proc: Procedure;
  plates: PlatesResponse | null;
  loading: boolean;
  airportIcao: string;
  onOpen?: (p: { cycle: string; pdfName: string; title: string; subtitle: string }) => void;
  georefMap: Map<string, PlateGeorefIndexRow>;
}

function PlateLink({ proc, plates, loading, airportIcao, onOpen, georefMap }: PlateLinkProps) {
  if (loading) return <span className="text-[10px] text-cockpit-500">…</span>;
  if (!plates || !onOpen) return null;
  const match = findPlateForProcedure({
    procIdent: proc.procIdent,
    routeType: proc.routeType,
    plates: plates.plates,
  });
  if (!match) return <span className="text-[10px] text-cockpit-500" title="No d-TPP plate matched">no plate</span>;
  const quality = plateGeorefQuality(georefMap.get(match.pdfName.toUpperCase()));
  return (
    <div className="flex items-center gap-1">
      <GeorefBadge quality={quality} showText={false} />
      <button
        type="button"
        onClick={() => onOpen({
          cycle: plates.cycle,
          pdfName: match.pdfName,
          title: match.chartName,
          subtitle: `${airportIcao} · ${proc.procIdent}`,
        })}
        className="flex items-center gap-1 text-[11px] text-ember-400 hover:text-ember-300"
      >
        <FileText size={11} />
        <span>Open plate</span>
      </button>
    </div>
  );
}

// ── Send-to-map link (boss request 2026-05-18) ─────────────────────────
// Lays the selected approach plate over the moving map using the existing
// georef overlay pipeline. Same lookup as PlateLink; flips to /map and
// pushes the spec into uiStore.activePlateOverlay where PlateOverlayLayer
// resolves the corners and renders the raster.
interface SendToMapLinkProps {
  proc: Procedure;
  plates: PlatesResponse | null;
  loading: boolean;
  airportIcao: string;
  georefMap: Map<string, PlateGeorefIndexRow>;
}

function SendToMapLink({ proc, plates, loading, airportIcao, georefMap }: SendToMapLinkProps) {
  const setPlateOverlay = useUiStore((s) => s.setPlateOverlay);
  const nav = useNavigate();
  if (loading) return null;
  if (!plates) return null;
  const match = findPlateForProcedure({
    procIdent: proc.procIdent,
    routeType: proc.routeType,
    plates: plates.plates,
  });
  if (!match) return null;
  const quality = plateGeorefQuality(georefMap.get(match.pdfName.toUpperCase()));
  // Match the ProcedureTab pattern: surface a hint in the tooltip when
  // there's no automated georef so the pilot knows the overlay will fall
  // back to the (rough) CIFP bbox.
  const tip = (quality === 'failed' || quality === 'unknown')
    ? 'Send to map (no automated georef — will use CIFP bbox)'
    : 'Send to map';
  return (
    <button
      type="button"
      title={tip}
      onClick={() => {
        setPlateOverlay({
          cycle: plates.cycle,
          pdfName: match.pdfName,
          chartName: match.chartName,
          airportIdent: airportIcao,
        });
        nav('/map');
      }}
      className="flex items-center gap-1 text-[11px] text-ember-400 hover:text-ember-300"
    >
      <MapIcon size={11} />
      <span>Send to map</span>
    </button>
  );
}

/** Human-readable label for a procedure in the picker dropdown.
 *
 * SID / STAR  → the CIFP procIdent as-is (CIITY3, ALWYS3 — those names
 *               are already the canonical published name).
 *
 * IAP         → spell out the approach type so 'L29R' (LOC RWY 29R)
 *               doesn't look like 'left-29-right'. Format:
 *                 'ILS 29R'           — I29R
 *                 'LOC 29R'           — L29R
 *                 'RNAV 29R'          — R29R
 *                 'RNAV-RNP Z 10R'    — H10RZ
 *                 'VOR 31'            — V31
 */
function procedureLabel(p: Procedure): string {
  if (p.routeType === 'IAP') {
    const parsed = parseIapIdent(p.procIdent);
    if (!parsed) return p.procIdent;
    // For RNAV (RNP), show 'RNP' for compactness in the dropdown.
    const typeShort = parsed.approachType === 'RNAV (RNP)' ? 'RNAV-RNP' : parsed.approachType;
    const variant = parsed.variant ? ` ${parsed.variant}` : '';
    return `${typeShort}${variant} ${parsed.runway}`;
  }
  // SID / STAR: append the procedure role so boss can scan them like
  // IAPs (which already show "RNAV 14" etc.). 2026-05-17.
  // CIFP doesn't carry a structured ODP-vs-SID flag; the FAA naming
  // convention has ODPs end in numeric "#1", "#2", etc. with no
  // "DEPARTURE" suffix in the procIdent. We don't try to distinguish
  // here — anything classified routeType=SID gets "Departure".
  if (p.routeType === 'SID')  return `${p.procIdent} · Departure`;
  if (p.routeType === 'STAR') return `${p.procIdent} · Arrival`;
  return p.procIdent;
}
